import { animate, style, transition, trigger } from '@angular/animations';
import { AfterViewInit, Component, HostListener, Inject, OnDestroy, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, NavigationError, ResolveEnd, ResolveStart, Router } from '@angular/router';
import {
  MuiAnimationTiming,
  MuiHeroAreaService,
  MuiHeroContent,
  MuiScrollbarService
} from '@move/ui';
import { Observable, of, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { MmuUserInfoDetails } from '../../auth/models/mmu-user-info-details';
import { MmuUserService } from '../../auth/user-service/mmu-user.service';
import { MmuConnectionService } from '../../connection/service/mmu-connection.service';
import { MmuMainMenuMainEntry, MmuMainMenuService } from '../../main-menu';
import { MMU_APP_CONTEXT, MmuAppContext } from '../../mmu-app-context';
import { MmuTitleService } from '../../title/public_api';
import { MMU_MENU_ITEMS, MmuMenuDefinition } from './menus/mmu-menu-items';


@Component({
  selector: 'mmu-hero-menu-frame',
  templateUrl: './mmu-hero-menu-frame.component.html',
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['./mmu-hero-menu-frame.component.scss'],
  animations: [
    trigger('overlay', [
      transition(':enter', [style({ opacity: 0 }), animate(MuiAnimationTiming.MAIN_MENU_TRANSITION, style({ opacity: 1 }))]),
      transition(':leave', [animate(MuiAnimationTiming.MAIN_MENU_TRANSITION, style({ opacity: 0 }))])
    ]),
    trigger('menu', [
      transition('false => true', [
        style({ transform: 'translateX(-100%)' }),
        animate(MuiAnimationTiming.MAIN_MENU_TRANSITION, style({ transform: 'translateX(0)' }))
      ]),
      transition('true => false', [
        style({ transform: 'translateX(0)' }),
        animate(MuiAnimationTiming.MAIN_MENU_TRANSITION, style({ transform: 'translateX(-100%)' }))
      ])
    ])
  ]
})
export class MmuHeroMenuFrameComponent implements OnDestroy, AfterViewInit {
  profileMenuItems: MmuMainMenuMainEntry[];
  menuItems$: Observable<MmuMainMenuMainEntry[]>;

  online$: Observable<boolean>;
  content: MuiHeroContent | null = null;

  readonly user$: Observable<MmuUserInfoDetails>;
  readonly pageTitle$: Observable<string>;
  readonly mobileMenuOpen$: Observable<boolean>;
  readonly menuCollapsed$: Observable<boolean>;
  readonly resolvingRouteData$: Observable<boolean>;
  isFullscreen$: Observable<boolean>;

  private readonly _destroy$ = new Subject<void>();

  @HostListener('document:fullscreenchange')
  onFullscreenChange(): void {
    if (!document.fullscreenElement && this.heroAreaService.isFullscreen) {
      this.exitFullScreen();
    }
  }

  constructor(
    readonly menuService: MmuMainMenuService,
    @Inject(MMU_MENU_ITEMS) private menuItems: MmuMenuDefinition,
    @Inject(MMU_APP_CONTEXT) private appContext: MmuAppContext,
    private userService: MmuUserService,
    private titleService: MmuTitleService,
    private router: Router,
    private route: ActivatedRoute,
    private heroAreaService: MuiHeroAreaService,
    private _mmuConnectionService: MmuConnectionService,
    private scrollService: MuiScrollbarService,
  ) {
    this.online$ = this._mmuConnectionService.isOnline$;
    this.pageTitle$ = this.titleService.title;
    this.user$ = this.userService.user$;
    this.menuCollapsed$ = menuService.collapsed$;
    this.mobileMenuOpen$ = menuService.mobileOpen$;

    this.resolvingRouteData$ = this.router.events.pipe(
      map((event) => {
        switch (event.constructor) {
          case ResolveStart:
            return true;
          case ResolveEnd:
            return false;
          case NavigationError:
          default:
            return false;
        }
      })
    );

    this.profileMenuItems = this.setupProfileMenuItems();

    this.heroAreaService.heroContent$.subscribe((c) => (this.content = c));

    // update the user menu entry after the user is changed in profile settings
    this.userService.user$.subscribe((res) => {
      this.profileMenuItems[0].label = res.name || res.firstName || res.username;
    });

    this.isFullscreen$ = this.heroAreaService.isPageFullScreen$;

    this.menuItems$ = this.menuItems instanceof Observable ? this.menuItems : of(this.menuItems);
  }

  exitFullScreen(): void {
    this.heroAreaService.isFullscreen = false;
  }

  ngAfterViewInit(): void {
    const scrollToElement: string | null = this.route.snapshot.queryParamMap.get('scroll');
    if (scrollToElement) {
      this.scrollService.scrollToElement(scrollToElement);
    }
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  private setupProfileMenuItems(): MmuMainMenuMainEntry[] {
    const userDetails = this.userService.getUserDetails();

    return [
      {
        label: userDetails.name || userDetails.firstName || userDetails.username,
        link: '/user/settings',
        icon: 'user',
        isExternalLink: this.appContext !== MmuAppContext.CONTAINER,
        children: [
          {
            label: 'mainMenu.profile',
            link: '/user/settings',
            isExternalLink: this.appContext !== MmuAppContext.CONTAINER
          },
          {
            label: 'mainMenu.logoutLink',
            link: '/logout',
            isExternalLink: true
          }
        ]
      }
    ];
  }
}
